import React, { useState } from 'react';
import {
    BrainCircuit,
    Database,
    TrendingUp,
    Search,
    GitBranch,
    Cpu,
    Layers,
    Clock,
    Network,
    Eye,
    MessageSquare,
    Rocket,
    Award,
    CheckCircle,
    ArrowRight,
    Terminal,
    PlayCircle,
    FileText,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AIDataSciencePage = () => {
    const [activeModule, setActiveModule] = useState(0);
    const [openFAQ, setOpenFAQ] = useState(0);

    // Custom CSS for thin scrollbar
    const scrollbarStyles = `
        .thin-scrollbar::-webkit-scrollbar {
            width: 4px;
        }
        .thin-scrollbar::-webkit-scrollbar-track {
            background: transparent;
        }
        .thin-scrollbar::-webkit-scrollbar-thumb {
            background-color: #cbd5e1;
            border-radius: 20px;
        }
        .thin-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #cbd5e1 transparent;
        }
    `;

    const features = [
        "Trainer is a PhD in Machine Learning and Wipro Certified Data Scientist",
        "28+ years of academic + industry experience as Senior Data Scientist",
        "100% Practical training with business case studies",
        "Video recordings of sessions provided for revision",
        "Includes 150+ industry use cases in Data Science and 50+ use cases in AI",
        "Hands-on with Python, SQL, ML, DL, NLP, Deployment & Cloud"
    ];

    const modules = [
        {
            title: "Python Programming for Data Science",
            icon: Terminal,
            items: ["Python basics, control flow, functions", "Data structures: lists, tuples, sets, dictionaries", "NumPy, Pandas, Matplotlib, Seaborn", "OOP for ML, virtual environments"]
        },
        {
            title: "Databases, SQL & Data Pipelines",
            icon: Database,
            items: ["SQL fundamentals & analytical queries", "Joins, subqueries, window functions", "PostgreSQL, ETL basics, data extraction & loading"]
        },
        {
            title: "Mathematics & Statistics",
            icon: TrendingUp,
            items: ["Linear algebra essentials", "Probability & distributions", "Hypothesis testing", "Optimization basics"]
        },
        {
            title: "Exploratory Data Analysis (EDA)",
            icon: Search,
            items: ["Univariate & bivariate analytics", "Outlier detection & missing value treatment", "Data visualization dashboards"]
        },
        {
            title: "CRISP-ML(Q) Framework",
            icon: GitBranch,
            items: ["Business understanding → data understanding → modeling", "ML quality and documentation"]
        },
        {
            title: "Feature Engineering",
            icon: Layers,
            items: ["Encoding, scaling, transformations", "Imbalanced data techniques", "Feature selection & extraction"]
        },
        {
            title: "Supervised Machine Learning",
            icon: BrainCircuit,
            items: ["Linear, Logistic, Multilinear, Multinomial Regression", "KNN, Naive Bayes", "Decision Tree, Random Forest, XGBoost", "Regularization (Lasso, Ridge)"]
        },
        {
            title: "Unsupervised Machine Learning",
            icon: Network,
            items: ["Clustering (K-Means, Hierarchical, DBSCAN)", "Association rule mining (Apriori, FP-Growth)", "Recommendation systems", "PCA & dimensionality reduction"]
        },
        {
            title: "Time Series Analytics",
            icon: Clock,
            items: ["Trends & seasonality", "ARIMA, SARIMA, Prophet models", "Forecasting pipelines"]
        },
        {
            title: "Deep Learning Foundations",
            icon: Cpu,
            items: ["Neural networks & backpropagation", "Activation functions & optimizers", "MLP, training workflows"]
        },
        {
            title: "Computer Vision & Advanced AI",
            icon: Eye,
            items: ["CNN fundamentals", "Convolution & image filters", "YOLO, GANs", "RNN, LSTM, GRU"]
        },
        {
            title: "NLP & LLM-based AI",
            icon: MessageSquare,
            items: ["Classical NLP", "BERT & modern transformers", "RASA-based conversational AI"]
        },
        {
            title: "AI Engineering & Deployment",
            icon: Rocket,
            items: ["Model deployment using Flask & FastAPI", "Docker basics, cloud deployment", "Git, versioning, CI/CD introduction"]
        },
        {
            title: "Major Projects (Industry Focus)",
            icon: Award,
            items: ["2 Major Projects (AI + DS)", "4-week industry project", "End-to-end EDA → ML → Deployment"]
        }
    ];

    const faqs = [
        {
            question: "What are the prerequisites for this course?",
            answer: "Basic knowledge of programming (any language) is helpful but not mandatory. We start from the very basics of Python. Mathematics (stats/probability) is covered in the module."
        },
        {
            question: "Do you provide placement assistance?",
            answer: "Yes, we offer 100% placement support, including resume building, mock interviews, and connecting you with hiring partners upon successful course completion."
        },
        {
            question: "Is this training fully practical?",
            answer: "Absolutely. The course is designed with a 'Learning by Doing' approach. You will work on 150+ industry use cases and 2 major end-to-end capstone projects."
        },
        {
            question: "What tools and technologies will I learn?",
            answer: "You will master Python, SQL, Tableau/PowerBI, TensorFlow, Keras, Pytorch, OpenCV, NLTK, Docker, Git, and cloud deployment platforms."
        },
        {
            question: "Will I get a certificate?",
            answer: "Yes, upon completion of the course and projects, you will receive a professional certification in AI & Data Science that is recognized by the industry."
        }
    ];

    // Helper to render module details content
    const renderModuleDetails = (module) => (
        <div className="bg-brand-card flex flex-col h-full">
            {/* Header */}
            <div className="p-8 border-b border-gray-800 bg-brand-card/95 backdrop-blur-sm sticky top-0 z-20">
                <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                        {React.createElement(module.icon, { className: "text-indigo-600 h-5 w-5" })}
                    </div>
                    {/* <span className="text-brand-gold font-bold tracking-wide text-xs uppercase">Module Details</span> */}
                </div>
                <h3 className="text-2xl font-bold text-brand-textLight leading-tight">
                    {module.title}
                </h3>
            </div>

            {/* Content List */}
            <div className="p-6 md:p-8 overflow-y-auto flex-1 thin-scrollbar">
                <div className="space-y-1">
                    {module.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 rounded-lg hover:bg-brand-bg transition-colors group border border-transparent hover:border-gray-800">
                            <div className="flex items-center gap-4">
                                <div className="h-8 w-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 text-xs font-bold flex-shrink-0">
                                    {idx + 1}
                                </div>
                                <span className="text-brand-textLight font-medium text-sm group-hover:text-brand-textLight leading-relaxed">
                                    {item}
                                </span>
                            </div>
                            <div className="text-gray-300 group-hover:text-indigo-400 pl-4">
                                {idx % 2 === 0 ? <PlayCircle size={18} /> : <FileText size={18} />}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-brand-bg">
            <style>{scrollbarStyles}</style>
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-indigo-900 via-slate-900 text-brand-textLight py-24 px-6 sm:px-12 lg:px-24 overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
                    <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 rounded-full bg-brand-gold blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-brand-bg0 blur-3xl"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="lg:w-3/4">
                        <div className="inline-block px-4 py-2 bg-indigo-500/20 border border-indigo-400/30 rounded-full text-indigo-300 font-semibold text-sm mb-6 backdrop-blur-sm">
                            Professional Certification Program
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                            AI & Data Science <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#E91E63] to-pink-400">Professional Training</span>
                        </h1>
                        <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
                            Master the complete data pipeline from Python programming to deploying advanced AI models with our comprehensive, industry-focused curriculum.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link
                                to="/contact-us"
                                className="inline-flex justify-center items-center px-8 py-4 bg-brand-gold text-black font-bold rounded-lg shadow-lg hover:bg-brand-goldHover transition-all transform hover:-translate-y-1"
                            >
                                Enroll Now
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Link>
                            <a
                                href="/AIGenius_Brochure.pdf"
                                download="AIGenius_Brochure.pdf"
                                className="inline-flex justify-center items-center px-8 py-4 bg-brand-card/10 backdrop-blur-sm border border-white/20 text-brand-textLight font-semibold rounded-lg hover:bg-brand-card/20 transition-all"
                            >
                                Download Brochure
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto py-20 px-6 sm:px-12 lg:px-24">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
                    {/* Main Content (Features & Intro) */}
                    <div className="lg:col-span-2">
                        {/* Program Features */}
                        <div>
                            <h2 className="text-3xl font-bold text-brand-textLight mb-8">Program Features</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {features.map((feature, index) => (
                                    <div key={index} className="flex items-start bg-brand-card p-5 rounded-xl shadow-sm border border-gray-800">
                                        <CheckCircle className="text-brand-gold mt-1 mr-3 flex-shrink-0" size={20} />
                                        <p className="text-brand-textLight font-medium">{feature}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 space-y-6">
                            <div className="bg-brand-card text-brand-textLight p-8 rounded-2xl shadow-xl">
                                <h3 className="text-2xl font-bold mb-2">Upcoming Batch</h3>
                                <p className="text-indigo-400 mb-6 font-medium">Limited Seats Available</p>

                                <div className="space-y-4 mb-8">
                                    <div className="flex justify-between items-center border-b border-gray-700 pb-3">
                                        <span className="text-brand-textMuted">Duration</span>
                                        <span className="font-semibold">6 Months</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-gray-700 pb-3">
                                        <span className="text-brand-textMuted">Mode</span>
                                        <span className="font-semibold">Online / Offline</span>
                                    </div>
                                    <div className="flex justify-between items-center border-b border-gray-700 pb-3">
                                        <span className="text-brand-textMuted">Projects</span>
                                        <span className="font-semibold">10+ Capstone</span>
                                    </div>
                                </div>

                                <Link to="/contact-us" className="block w-full py-4 bg-brand-gold hover:bg-brand-goldHover text-center text-black font-bold rounded-xl transition-all shadow-lg shadow-pink-500/20">
                                    Reserve Your Seat
                                </Link>
                                <p className="text-center text-brand-textMuted text-xs mt-4">
                                    Speak to our career counselor today
                                </p>
                            </div>

                            <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100">
                                <h4 className="font-bold text-indigo-900 mb-2">Need content guidance?</h4>
                                <p className="text-indigo-700 text-sm mb-4">
                                    Download the detailed brochure to see the complete week-by-week curriculum plan.
                                </p>
                                <a 
                                    href="/AIGenius_Brochure.pdf" 
                                    download="AIGenius_Brochure.pdf"
                                    className="text-brand-gold font-semibold text-sm hover:underline flex items-center"
                                >
                                    Download Brochure <ArrowRight size={14} className="ml-1" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comprehensive Syllabus */}
                <div>
                    <div className="flex items-center justify-between mb-10">
                        <h2 className="text-3xl font-bold text-brand-textLight">Comprehensive Syllabus</h2>
                        <span className="hidden sm:inline-block px-4 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold">
                            14 Modules
                        </span>
                    </div>

                    <div className="flex flex-col md:flex-row bg-brand-card rounded-xl shadow-lg border border-gray-800 overflow-hidden min-h-[600px]">
                        {/* Left Sidebar: Module List */}
                        <div className="md:w-5/12 border-r border-gray-800 bg-brand-bg/30 overflow-y-auto max-h-[600px] thin-scrollbar">
                            {modules.map((module, index) => (
                                <React.Fragment key={index}>
                                    <button
                                        onClick={() => setActiveModule(activeModule === index ? -1 : index)}
                                        className={`w-full text-left p-5 border-b border-gray-800 transition-all duration-200 flex justify-between items-center group ${activeModule === index
                                            ? 'bg-brand-card border-l-4 border-l-[#E91E63] shadow-sm z-10 relative'
                                            : 'hover:bg-brand-card hover:border-l-4 hover:border-l-indigo-200 border-l-4 border-l-transparent'
                                            }`}
                                    >
                                        <div className="pr-4">
                                            <span className={`text-xs font-bold uppercase tracking-wider block mb-1.5 ${activeModule === index ? 'text-brand-gold' : 'text-brand-textMuted'
                                                }`}>
                                                Module {index + 1}
                                            </span>
                                            <h3 className={`text-sm font-bold leading-snug ${activeModule === index ? 'text-brand-textLight' : 'text-brand-textMuted'
                                                }`}>
                                                {module.title}
                                            </h3>
                                        </div>
                                        <div className="flex items-center">
                                            {/* Mobile Chevron */}
                                            {activeModule === index ? (
                                                <ChevronUp className="md:hidden text-brand-gold mr-2" size={20} />
                                            ) : (
                                                <ChevronDown className="md:hidden text-gray-300 mr-2" size={20} />
                                            )}

                                            {/* Desktop Arrow */}
                                            <ArrowRight size={16} className={`hidden md:block transform transition-all duration-200 flex-shrink-0 ${activeModule === index ? 'text-brand-gold opacity-100 translate-x-0' : 'text-gray-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                                                }`} />
                                        </div>
                                    </button>

                                    {/* Mobile Only: Accordion Content */}
                                    <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out bg-brand-bg border-b border-gray-800 ${activeModule === index ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                                        }`}>
                                        {activeModule === index && renderModuleDetails(module)}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>

                        {/* Right Content: Module Details (Desktop Only) */}
                        <div className="hidden md:flex md:w-7/12 bg-brand-card flex-col relative border-l border-gray-800">
                            {modules[activeModule] ? (
                                renderModuleDetails(modules[activeModule])
                            ) : (
                                <div className="flex h-full items-center justify-center p-8 text-brand-textMuted">
                                    <div className="text-center opacity-50">
                                        <Database className="mx-auto h-12 w-12 mb-3" />
                                        <p className="font-medium">Select a module to view details</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* FAQ Section */}
                <div className="mt-20 mb-16">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-brand-textLight mb-4">Frequently Asked Questions</h2>
                        <p className="text-brand-textMuted max-w-2xl mx-auto">
                            Got questions? We have answers. If you don't see your question here, slightly feel free to contact us.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-brand-card border border-gray-800 rounded-xl overflow-hidden transition-all duration-300 hover:shadow-md"
                            >
                                <button
                                    onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                                    className="w-full flex items-center justify-between p-6 text-left bg-brand-card hover:bg-brand-bg transition-colors"
                                >
                                    <span className="font-semibold text-brand-textLight pr-8">{faq.question}</span>
                                    {openFAQ === index ? (
                                        <ChevronUp className="flex-shrink-0 text-brand-gold" />
                                    ) : (
                                        <ChevronDown className="flex-shrink-0 text-brand-textMuted" />
                                    )}
                                </button>
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${openFAQ === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <div className="p-6 pt-0 text-brand-textMuted leading-relaxed border-t border-gray-50">
                                        {faq.answer}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AIDataSciencePage;
